import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AutomationFlowListItem,
  AutomationFlowListQuery,
  AutomationFlowResource,
  CloneAutomationFlowInput,
  CollectionMeta,
  CreateAutomationFlowInput,
  UpdateAutomationFlowInput,
  UpdateMyAutomationFlowInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['automation-flows'] as const;
const ADMIN_KEY = ['admin', 'automation-flows'] as const;

export interface AutomationFlowListPage {
  items: AutomationFlowListItem[];
  meta: CollectionMeta;
}

// --- Tenant-facing ------------------------------------------------------

function toQuery(
  q: AutomationFlowListQuery,
): Record<string, string | number | boolean | undefined> {
  return { page: q.page, pageSize: q.pageSize, status: q.status, mine: q.mine };
}

export async function listAutomationFlows(
  q: AutomationFlowListQuery = {},
): Promise<AutomationFlowListPage> {
  const res = await apiClient.getCollection<AutomationFlowListItem>('/automation-flows', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}

export function useAutomationFlows(query: AutomationFlowListQuery = {}) {
  return useQuery<AutomationFlowListPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listAutomationFlows(query),
  });
}

export const getAutomationFlow = (id: string) =>
  apiClient.get<AutomationFlowResource>(`/automation-flows/${id}`);

export function useAutomationFlow(id: string | undefined) {
  return useQuery<AutomationFlowResource, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getAutomationFlow(id!),
    enabled: Boolean(id),
  });
}

export const cloneAutomationFlow = (id: string, input: CloneAutomationFlowInput) =>
  apiClient.post<AutomationFlowResource>(`/automation-flows/${id}/clone`, input);
export const updateMyAutomationFlow = (id: string, input: UpdateMyAutomationFlowInput) =>
  apiClient.patch<AutomationFlowResource>(`/automation-flows/${id}`, input);
export const deleteMyAutomationFlow = (id: string) =>
  apiClient.delete<void>(`/automation-flows/${id}`);

export function useCloneAutomationFlow() {
  const qc = useQueryClient();
  return useMutation<
    AutomationFlowResource,
    ApiClientError,
    { id: string; input: CloneAutomationFlowInput }
  >({
    mutationFn: ({ id, input }) => cloneAutomationFlow(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateMyAutomationFlow(id: string) {
  const qc = useQueryClient();
  return useMutation<AutomationFlowResource, ApiClientError, UpdateMyAutomationFlowInput>({
    mutationFn: (input) => updateMyAutomationFlow(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteMyAutomationFlow() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteMyAutomationFlow,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// --- Platform admin ----------------------------------------------------

export async function listAdminAutomationFlows(
  q: AutomationFlowListQuery = {},
): Promise<AutomationFlowListPage> {
  const res = await apiClient.getCollection<AutomationFlowListItem>('/admin/automation-flows', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const getAdminAutomationFlow = (id: string) =>
  apiClient.get<AutomationFlowResource>(`/admin/automation-flows/${id}`);
export const createAutomationFlow = (input: CreateAutomationFlowInput) =>
  apiClient.post<AutomationFlowResource>('/admin/automation-flows', input);
export const updateAutomationFlow = (id: string, input: UpdateAutomationFlowInput) =>
  apiClient.patch<AutomationFlowResource>(`/admin/automation-flows/${id}`, input);
export const deleteAutomationFlow = (id: string) =>
  apiClient.delete<void>(`/admin/automation-flows/${id}`);

export function useAdminAutomationFlows(query: AutomationFlowListQuery = {}) {
  return useQuery<AutomationFlowListPage, ApiClientError>({
    queryKey: [...ADMIN_KEY, 'list', query],
    queryFn: () => listAdminAutomationFlows(query),
  });
}

export function useAdminAutomationFlow(id: string | undefined) {
  return useQuery<AutomationFlowResource, ApiClientError>({
    queryKey: [...ADMIN_KEY, 'detail', id],
    queryFn: () => getAdminAutomationFlow(id!),
    enabled: Boolean(id),
  });
}

export function useCreateAutomationFlow() {
  const qc = useQueryClient();
  return useMutation<AutomationFlowResource, ApiClientError, CreateAutomationFlowInput>({
    mutationFn: createAutomationFlow,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateAutomationFlow(id: string) {
  const qc = useQueryClient();
  return useMutation<AutomationFlowResource, ApiClientError, UpdateAutomationFlowInput>({
    mutationFn: (input) => updateAutomationFlow(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteAutomationFlow() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteAutomationFlow,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
